import type { TFunction } from "i18next";

import { ColumnDataType } from "shared/api/schemaDefinitions/types";
import {
  DATE_WITH_TIME_AND_ZONE_FORMAT,
  DATE_WITH_TIME_FORMAT,
  DATE_WITH_TIME_NO_SECONDS,
  DisplayDateFormat,
  HOUR_ONLY,
  LONG_DATE_FORMAT,
  MONTH_DAY,
  MONTH_DAY_NUMERIC,
  MONTH_ONLY,
  MONTH_YEAR,
  MONTH_YEAR_DAY,
  MONTH_YEAR_FULL,
  MONTH_YEAR_SHORT,
  SHORT_DATE_FORMAT,
  TIME_ONLY,
  TIME_WITH_SECONDS,
  VERY_SHORT_DATE_FORMAT,
  YEAR_ONLY,
} from "shared/constants";
import { DataType } from "shared/types";

import { SelectOption } from "features/ui/Select";

export const DATETIME_TYPES: ColumnDataType[] = ["datetime", "datetime_in_utc"];

export const DATE_LIKE_TYPES: ColumnDataType[] = [
  "date",
  "datetime",
  "datetime_in_utc",
];

// The display formats an attribute's dateFormat can be set to. A date column offers the first list, a datetime the
// first two, and a datetime_in_utc all three, so adding a constant to one list is all it takes to offer it.
const DATE_ONLY_FORMATS: DisplayDateFormat[] = [
  SHORT_DATE_FORMAT,
  LONG_DATE_FORMAT,
  VERY_SHORT_DATE_FORMAT,
  MONTH_YEAR_DAY,
  MONTH_YEAR,
  MONTH_YEAR_FULL,
  MONTH_YEAR_SHORT,
  MONTH_DAY,
  MONTH_ONLY,
  YEAR_ONLY,
];
const TIME_FORMATS: DisplayDateFormat[] = [
  DATE_WITH_TIME_FORMAT,
  DATE_WITH_TIME_NO_SECONDS,
  TIME_ONLY,
  TIME_WITH_SECONDS,
  HOUR_ONLY,
];
const ZONED_FORMATS: DisplayDateFormat[] = [DATE_WITH_TIME_AND_ZONE_FORMAT];

export const getDateFormatsForType = (
  dataType: ColumnDataType
): DisplayDateFormat[] => {
  switch (dataType) {
    case "date":
      return DATE_ONLY_FORMATS;
    case "datetime":
      return [...DATE_ONLY_FORMATS, ...TIME_FORMATS];
    case "datetime_in_utc":
      return [...DATE_ONLY_FORMATS, ...TIME_FORMATS, ...ZONED_FORMATS];
    default:
      return [];
  }
};

// Keyed by every display format, so a new constant cannot be offered without a label.
export const getDateFormatLabels = (
  t: TFunction
): Record<DisplayDateFormat, string> => ({
  [SHORT_DATE_FORMAT]: t(
    "superadmin:attributes_config.format.date_formats.short_date"
  ),
  [LONG_DATE_FORMAT]: t(
    "superadmin:attributes_config.format.date_formats.long_date"
  ),
  [VERY_SHORT_DATE_FORMAT]: t(
    "superadmin:attributes_config.format.date_formats.very_short_date"
  ),
  [MONTH_YEAR_DAY]: t(
    "superadmin:attributes_config.format.date_formats.month_year_day"
  ),
  [MONTH_YEAR]: t(
    "superadmin:attributes_config.format.date_formats.month_year"
  ),
  [MONTH_YEAR_FULL]: t(
    "superadmin:attributes_config.format.date_formats.month_year_full"
  ),
  [MONTH_YEAR_SHORT]: t(
    "superadmin:attributes_config.format.date_formats.month_year_short"
  ),
  [MONTH_DAY]: t("superadmin:attributes_config.format.date_formats.month_day"),
  [MONTH_DAY_NUMERIC]: t(
    "superadmin:attributes_config.format.date_formats.month_day_numeric"
  ),
  [MONTH_ONLY]: t("common:month"),
  [YEAR_ONLY]: t("common:year"),
  [DATE_WITH_TIME_FORMAT]: t(
    "superadmin:attributes_config.format.date_formats.date_with_time"
  ),
  [DATE_WITH_TIME_NO_SECONDS]: t(
    "superadmin:attributes_config.format.date_formats.date_with_time_no_seconds"
  ),
  [TIME_ONLY]: t("pages:localization.preferences.examples.time"),
  [TIME_WITH_SECONDS]: t(
    "superadmin:attributes_config.format.date_formats.time_with_seconds"
  ),
  [HOUR_ONLY]: t("pages:localization.preferences.examples.hour"),
  [DATE_WITH_TIME_AND_ZONE_FORMAT]: t(
    "superadmin:attributes_config.format.date_formats.date_with_time_and_zone"
  ),
});

// How a column of each type renders and filters in a table. Only OBJECT serializes a json value that arrives
// parsed; every other type renders it as "[object Object]".
export const TABLE_DATA_TYPES: Record<ColumnDataType, DataType> = {
  string: DataType.STRING,
  int: DataType.NUMBER,
  float: DataType.NUMBER,
  boolean: DataType.BOOLEAN,
  date: DataType.DATE,
  datetime: DataType.DATE_WITH_TIME_NO_TZ,
  datetime_in_utc: DataType.DATE_WITH_TIME_UTC,
  json: DataType.OBJECT,
  string_array: DataType.STRING,
};

// The ClickHouse functions a date-like column can be ordered or partitioned by, in the order the selectors offer
// them. "" stands for the column itself, unwrapped.
export const getExprFunctionOptions = (
  t: TFunction
): SelectOption<string>[] => [
  { id: "", value: t("common:none") },
  { id: "toYYYYMM", value: "toYYYYMM" },
  { id: "toYYYYMMDD", value: "toYYYYMMDD" },
  { id: "toYear", value: "toYear" },
  { id: "toMonth", value: "toMonth" },
  { id: "toDate", value: "toDate" },
  { id: "toStartOfMonth", value: "toStartOfMonth" },
  { id: "toStartOfWeek", value: "toStartOfWeek" },
  { id: "toStartOfDay", value: "toStartOfDay" },
];

// The registry's table types, in the order the selectors offer them.
export const getTableTypeOptions = (t: TFunction): SelectOption<string>[] => [
  { id: "event", value: t("superadmin:data_sources.table_type.event") },
  { id: "asset", value: t("superadmin:data_sources.table_type.asset") },
  { id: "catalog", value: t("superadmin:data_sources.table_type.catalog") },
];

// The schema registry's data types, in the order the type selectors offer them. The options are read off these keys,
// so a new ColumnDataType has to be given a label here — an order list of its own could leave one out of the
// selectors without failing tsc.
const getDataTypeLabels = (t: TFunction): Record<ColumnDataType, string> => ({
  string: t("superadmin:data_sources.data_type.string"),
  int: t("superadmin:data_sources.data_type.int"),
  float: t("superadmin:data_sources.data_type.float"),
  boolean: t("superadmin:data_sources.data_type.boolean"),
  date: t("superadmin:data_sources.data_type.date"),
  datetime: t("superadmin:data_sources.data_type.datetime"),
  datetime_in_utc: t("superadmin:data_sources.data_type.datetime_in_utc"),
  json: t("superadmin:data_sources.data_type.json"),
  string_array: t("superadmin:data_sources.data_type.string_array"),
});

export const getDataTypeLabel = (
  t: TFunction,
  dataType: ColumnDataType
): string => getDataTypeLabels(t)[dataType];

// Every type a source column can be read as, which is what the mapping grid offers: the type there says how the
// source's values are cast as well as what the column becomes.
export const getDataTypeOptions = (
  t: TFunction
): SelectOption<ColumnDataType>[] => {
  const labels = getDataTypeLabels(t);

  return (Object.keys(labels) as ColumnDataType[]).map((id) => ({
    id,
    value: labels[id],
  }));
};
